import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Filter, Gamepad2, Loader2, Search, Swords, Trophy, User, Users } from "lucide-react";
import { useMemo } from "react";

import { MatchRow, MatchRowSkeleton } from "../../../../components/match/MatchRow";
import { Breadcrumbs, Button, SegmentedControl, Select } from "../../../../components/ui";
import { useSession } from "../../../../lib/auth-client";
import { trpc } from "../../../../lib/trpc";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/app/clubs/$slug/matches")({
  component: MatchHistoryPage,
  validateSearch: (search: Record<string, unknown>) => ({
    clubGameId: typeof search.clubGameId === "string" ? search.clubGameId : undefined,
    queueId: typeof search.queueId === "string" ? search.queueId : undefined,
    userId: typeof search.userId === "string" ? search.userId : undefined,
    me: search.me === false ? false : true,
    period: typeof search.period === "string" ? search.period : undefined,
  }),
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const periodOptions = [
  { value: "__all__", label: "All" },
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
];

const periodLabels: Record<string, string> = {
  "24h": "in the last 24 hours",
  "7d": "in the last 7 days",
  "30d": "in the last 30 days",
  "90d": "in the last 90 days",
};

function getDateGroup(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today);
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  if (d >= today) return "Today";
  if (d >= yesterday) return "Yesterday";
  if (d >= weekAgo) return "This Week";
  if (d >= monthAgo) return "This Month";
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** Returns ms offset for a period key, or undefined for "all time". */
function periodToMs(period?: string): number | undefined {
  switch (period) {
    case "24h":
      return 24 * 60 * 60 * 1000;
    case "7d":
      return 7 * 24 * 60 * 60 * 1000;
    case "30d":
      return 30 * 24 * 60 * 60 * 1000;
    case "90d":
      return 90 * 24 * 60 * 60 * 1000;
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function MatchHistoryPage() {
  const { slug } = Route.useParams();
  const {
    clubGameId: searchGameId,
    queueId: searchQueueId,
    userId: searchUserId,
    me: searchMe,
    period: searchPeriod,
  } = Route.useSearch();

  const navigate = Route.useNavigate();

  const { data: club, isLoading: clubLoading } = trpc.club.get.useQuery({ slug });
  const { data: session } = useSession();
  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const isMember = myClubs?.some((c) => c.slug === slug) ?? false;

  // If "me" is toggled on AND no explicit userId, use the current user (members only)
  const isMyMatches = isMember && searchMe && !searchUserId;
  const effectiveUserId = searchUserId ?? (isMyMatches ? session?.user?.id : undefined);

  // Fetch viewed player's name when looking at another user's matches
  const { data: viewedPlayer } = trpc.user.getProfile.useQuery(
    { id: searchUserId! },
    { enabled: !!searchUserId },
  );

  // Use URL search params as single source of truth — no local state duplication
  const selectedGameId = searchGameId;
  const selectedQueueId = searchQueueId;
  const selectedPeriod = searchPeriod;

  // Stable date — only recomputed when the period key changes, not every render
  const afterDate = useMemo(() => {
    const ms = periodToMs(selectedPeriod);
    if (!ms) return undefined;
    // Round to nearest minute so it doesn't flicker between renders
    const now = Math.floor(Date.now() / 60_000) * 60_000;
    return new Date(now - ms);
  }, [selectedPeriod]);

  const hasActiveFilters = !!selectedGameId || !!selectedQueueId || !!selectedPeriod;

  const { data: games, isLoading: gamesLoading } = trpc.clubGame.list.useQuery(
    { clubId: club?.id ?? "" },
    { enabled: !!club?.id },
  );

  const activeGameId = selectedGameId ?? games?.[0]?.id;

  const { data: queues } = trpc.queue.list.useQuery(
    { clubId: club?.id ?? "", clubGameId: activeGameId! },
    { enabled: !!club?.id && !!activeGameId },
  );

  const {
    data: history,
    isLoading: historyLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.match.history.useInfiniteQuery(
    {
      clubId: club?.id ?? "",
      clubGameId: selectedGameId,
      queueId: selectedQueueId,
      userId: effectiveUserId,
      after: afterDate,
      limit: 20,
    },
    {
      enabled: !!club?.id && (isMyMatches ? !!session?.user?.id : true),
      getNextPageParam: (last) => last.nextCursor,
      placeholderData: keepPreviousData,
    },
  );

  const items = useMemo(() => history?.pages.flatMap((p) => p.items) ?? [], [history]);

  // Group by date
  const groupedMatches = useMemo(() => {
    const groups: { label: string; matches: typeof items }[] = [];
    let currentLabel = "";
    for (const match of items) {
      const label = getDateGroup(match.completedAt ?? match.createdAt);
      if (label !== currentLabel) {
        currentLabel = label;
        groups.push({ label, matches: [] });
      }
      groups[groups.length - 1].matches.push(match);
    }
    return groups;
  }, [items]);

  // When all matches share the same queue, hide redundant queue/game info per row
  const isCompact = useMemo(() => {
    if (items.length === 0) return false;
    const first = items[0].queueName;
    return items.every((m) => m.queueName === first);
  }, [items]);

  const handleGameChange = (gameId: string) => {
    const newGameId = gameId === "__all__" ? undefined : gameId;
    void navigate({ search: (prev) => ({ ...prev, clubGameId: newGameId, queueId: undefined }) });
  };

  const handleQueueChange = (queueId: string) => {
    const newQueueId = queueId === "__all__" ? undefined : queueId;
    void navigate({ search: (prev) => ({ ...prev, queueId: newQueueId }) });
  };

  const handlePlayerFilterChange = (value: string) => {
    const newMe = value === "me";
    void navigate({ search: (prev) => ({ ...prev, me: newMe }) });
  };

  const handlePeriodChange = (period: string) => {
    const newPeriod = period === "__all__" ? undefined : period;
    void navigate({ search: (prev) => ({ ...prev, period: newPeriod }) });
  };

  const handleClearFilters = () => {
    void navigate({
      search: (prev) => ({
        ...prev,
        clubGameId: undefined,
        queueId: undefined,
        period: undefined,
        me: true,
      }),
    });
  };

  // Subtitle text
  const subtitle = searchUserId
    ? viewedPlayer
      ? `${viewedPlayer.name}'s completed matches`
      : "Loading player..."
    : isMyMatches
      ? "Your completed matches"
      : isMember
        ? "All completed matches"
        : "Completed matches in this club";

  if (clubLoading || gamesLoading) return <MatchHistorySkeleton />;
  if (!club) return null;

  const gameOptions = [
    { value: "__all__", label: "All Games" },
    ...(games ?? []).map((g) => ({ value: g.id, label: g.name })),
  ];

  const queueOptions = [
    { value: "__all__", label: "All Queues" },
    ...(queues ?? []).map((q) => ({ value: q.id, label: q.name })),
  ];

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="space-y-3">
        <Breadcrumbs
          items={[
            { label: club.name, to: "/app/clubs/$slug", params: { slug } },
            { label: "Match History" },
          ]}
        />
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Match History</h1>
          <p className="text-sm text-surface-500 mt-0.5">{subtitle}</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {isMember && (
          <SegmentedControl
            size="sm"
            value={isMyMatches ? "me" : "all"}
            onChange={handlePlayerFilterChange}
            options={[
              {
                value: "me",
                label: (
                  <span className="flex items-center gap-1.5">
                    <User className="size-3" />
                    My Matches
                  </span>
                ),
              },
              {
                value: "all",
                label: (
                  <span className="flex items-center gap-1.5">
                    <Users className="size-3" />
                    All
                  </span>
                ),
              },
            ]}
          />
        )}
        {games && games.length > 1 && (
          <Select
            options={gameOptions}
            value={selectedGameId ?? "__all__"}
            onChange={handleGameChange}
            placeholder="Select game"
            icon={<Gamepad2 className="size-4 text-surface-500" />}
            size="sm"
            className="bg-surface-850 w-auto"
          />
        )}
        {queues && queues.length > 0 && (
          <Select
            options={queueOptions}
            value={selectedQueueId ?? "__all__"}
            onChange={handleQueueChange}
            placeholder="Select queue"
            icon={<Swords className="size-4 text-surface-500" />}
            size="sm"
            className="bg-surface-850 w-auto"
          />
        )}
        <div className="hidden md:block flex-1" />
        <div className="flex items-center gap-1 p-0.5 rounded-lg bg-surface-800/60 border border-surface-700/40">
          {periodOptions.map((opt) => {
            const isActive = (selectedPeriod ?? "__all__") === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handlePeriodChange(opt.value)}
                className={`px-2.5 py-1.5 rounded-md text-xs font-bold transition-all duration-150 ${
                  isActive
                    ? "bg-surface-700 text-surface-100"
                    : "text-surface-500 hover:text-surface-300 hover:bg-surface-700/30"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Match list */}
      {historyLoading ? (
        <MatchListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState
          isMyMatches={isMyMatches}
          hasActiveFilters={hasActiveFilters}
          periodKey={selectedPeriod}
          onClearFilters={handleClearFilters}
        />
      ) : (
        <div className="space-y-0.5">
          {groupedMatches.map((group, gi) => (
            <div key={group.label}>
              <div className={`flex items-center gap-1.5 pb-2 ${gi === 0 ? "" : "pt-2"}`}>
                <span className="text-2xs font-bold uppercase tracking-widest text-surface-500 shrink-0">
                  {group.label}
                </span>
                <span className="text-2xs font-bold tabular-nums text-surface-400 bg-surface-700/40 px-1.5 py-0.5 rounded shrink-0">
                  {group.matches.length}
                </span>
                <div className="flex-1 h-px bg-surface-700/30" />
              </div>
              <div className="space-y-1">
                {group.matches.map((match) => (
                  <MatchRow
                    key={match.id}
                    match={match}
                    showResult
                    compact={isCompact}
                    viewerUserId={session?.user?.id}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Load more */}
          {hasNextPage && (
            <div className="flex justify-center pt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                leftIcon={
                  isFetchingNextPage ? <Loader2 className="size-3.5 animate-spin" /> : undefined
                }
              >
                {isFetchingNextPage ? "Loading..." : "Load More"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------

function EmptyState({
  isMyMatches,
  hasActiveFilters,
  periodKey,
  onClearFilters,
}: {
  isMyMatches: boolean;
  hasActiveFilters: boolean;
  periodKey?: string;
  onClearFilters: () => void;
}) {
  // Filters are active but returned no results
  if (hasActiveFilters) {
    const timeLabel = periodKey && periodLabels[periodKey] ? periodLabels[periodKey] : null;
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-surface-800 border border-surface-700/40 mb-4">
          <Search className="size-6 text-surface-500" />
        </div>
        <h3 className="text-lg font-bold text-white">No matches found</h3>
        <p className="text-sm text-surface-500 mt-1 max-w-xs">
          {timeLabel
            ? `No completed matches ${timeLabel} with these filters.`
            : "No completed matches for the selected filters."}
        </p>
        <Button variant="ghost" size="sm" className="mt-4" onClick={onClearFilters}>
          <Filter className="size-3.5 mr-1.5" />
          Clear Filters
        </Button>
      </div>
    );
  }

  // No matches at all
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-surface-800 border border-surface-700/40 mb-4">
        <Trophy className="size-6 text-surface-500" />
      </div>
      <h3 className="text-lg font-bold text-white">No matches yet</h3>
      <p className="text-sm text-surface-500 mt-1 max-w-xs">
        {isMyMatches
          ? "Queue up for a match to start building your history."
          : "No completed matches in this club yet."}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function MatchHistorySkeleton() {
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      <div className="space-y-1.5">
        <div className="h-7 w-40 rounded bg-surface-700/40 animate-pulse" />
        <div className="h-4 w-56 rounded bg-surface-700/25 animate-pulse" />
      </div>
      <div className="flex gap-2">
        <div className="h-8 w-40 rounded-lg bg-surface-700/30 animate-pulse" />
        <div className="h-8 w-40 rounded-lg bg-surface-700/30 animate-pulse" />
        <div className="h-8 w-40 rounded-lg bg-surface-700/30 animate-pulse" />
        <div className="h-8 w-40 rounded-lg bg-surface-700/30 animate-pulse" />
      </div>
      <MatchListSkeleton />
    </div>
  );
}

function MatchListSkeleton() {
  return (
    <div className="space-y-1.5">
      {Array.from({ length: 8 }).map((_, i) => (
        <MatchRowSkeleton key={i} />
      ))}
    </div>
  );
}
